import type { SceneGroup } from '@lumen5/luminary';

import { $matchBoundaryNodes } from './lexical-boundary';
import { partitionTranscriptByScene } from './transcript-partition';
import { getInterleavedTranscript, interleavedTokenToString } from './transcript-tokens';

import { getEditorFromSceneGroupId } from 'js/src/data/transcript';
import { $getTimeIndicatedBy } from 'js/src/data/transcript/transcript-operations';
import { checkpointVideo } from 'js/src/data/video-data';
import uuid from 'js/src/libs/uuid';
import { saveRawLuminary } from 'js/src/luminary';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    moveOverlaysAwayFromTimesOperation,
    translateOverlayEndOperation,
    translateOverlayStartOperation,
} from 'js/src/luminary/luminary-operations';
import {
    getBaseSceneDesignId,
    isAIAvatarSceneGroup,
    isSceneAnOverlay,
    isTalkingHeadSceneGroup,
} from 'js/src/luminary/luminary-selectors';
import { runVoicedrivenTranscriptBatchOperation } from 'js/src/luminary/voicedriven-scenes';

export type OverlayWordRange =
    | { startTimeMillis: number; endTimeMillis: number }
    | { error: string };

/**
 * Resolve a sequence of transcript words to the transcript-time range they occupy, via the same
 * lexical helper the UI uses (`$getTimeIndicatedBy`), so an agent-declared range snaps to the same
 * boundaries a word selection would. This is the placement math WITHOUT the overlap rejection: shared
 * by add_voiceover_scene / add_chart_overlay (which reject collisions via {@link
 * resolveOverlayRangeOperation}) and set_scene_range (which re-times an overlay and must not reject
 * overlapping itself). `requestedWords` may include `<pause seconds="N"/>` tags; returns `{ error }`
 * on any problem.
 */
export const resolveOverlayWordRangeOperation = createLuminaryOperation(
    ({ getState, call }, sceneGroup: SceneGroup, requestedWords: string[]): OverlayWordRange => {
        if (requestedWords.length === 0) {
            return { error: 'words array cannot be empty. Please provide at least one word.' };
        }

        const editor = getEditorFromSceneGroupId(getState(), sceneGroup.id);
        if (!editor) {
            return { error: 'The transcript editor for this scene group is not available.' };
        }

        return editor.getEditorState().read((): OverlayWordRange => {
            const nodeSpan = $matchBoundaryNodes(requestedWords);
            if ('error' in nodeSpan) {
                return nodeSpan;
            }
            const { startTime, endTime } = call(
                $getTimeIndicatedBy,
                nodeSpan.firstNode,
                nodeSpan.lastNode,
                sceneGroup.id,
            );
            return { startTimeMillis: startTime, endTimeMillis: endTime };
        });
    },
);

/**
 * {@link resolveOverlayWordRangeOperation} plus rejection of ranges that collide with an existing
 * overlay, for placing a NEW overlay (add_voiceover_scene, add_chart_overlay). Overlap ranges come
 * from the shared partition, so this agrees with the coverage the agent sees.
 */
export const resolveOverlayRangeOperation = createLuminaryOperation(
    ({ getState, call }, sceneGroup: SceneGroup, requestedWords: string[]): OverlayWordRange => {
        const range = call(resolveOverlayWordRangeOperation, sceneGroup, requestedWords);
        if ('error' in range) {
            return range;
        }
        const { startTimeMillis, endTimeMillis } = range;

        const partition = partitionTranscriptByScene(getState(), sceneGroup);
        const existingOverlayRanges = (partition ?? []).filter((entry) => entry.role === 'overlay');
        for (const overlay of existingOverlayRanges) {
            const hasOverlap = !(
                endTimeMillis <= overlay.startMillis || startTimeMillis >= overlay.endMillis
            );
            if (hasOverlap) {
                const affectedWords = getInterleavedTranscript(getState(), sceneGroup)
                    .filter(
                        (token) =>
                            !(
                                token.endTime <= overlay.startMillis ||
                                token.startTime >= overlay.endMillis
                            ),
                    )
                    .map(interleavedTokenToString);
                return {
                    error: `The requested words "${requestedWords.join(' ')}" overlap with an existing overlay. The existing overlay covers these words: "${affectedWords.join(' ')}". Please choose a different word range that doesn't overlap with existing overlays.`,
                };
            }
        }

        return { startTimeMillis, endTimeMillis };
    },
);

/**
 * Re-time an overlay to cover exactly `[startTimeMillis, endTimeMillis]` (transcript time), keeping
 * its identity/content. Clears other overlays from the region, moves both edges, then — for voiceover
 * only — stretches an adjacent overlay to fill any shrunk edge, mirroring the drag handles' "sticky"
 * behaviour (OverlayDragHandlePlugin's `handlesAreSticky`); talking-head / AI-avatar leave the base
 * showing. Edges move end-first when growing so the scene never collapses to zero (which deletes it).
 */
export const setSceneRangeOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sceneId: string,
        sceneGroupId: string,
        startTimeMillis: number,
        endTimeMillis: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new Error('Scene group missing');
        }
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('Scene missing');
        }
        const baseSceneDesignId = getBaseSceneDesignId(getState(), sceneGroup);
        if (!baseSceneDesignId) {
            throw new Error('Base scene design id not found');
        }

        const state = getState();
        const currentEntry = partitionTranscriptByScene(state, sceneGroup)?.find(
            (entry) => entry.scene.id === sceneId,
        );
        const currentStartMillis = currentEntry?.startMillis;
        const currentEndMillis = currentEntry?.endMillis;
        const growingEnd = currentEndMillis === undefined || endTimeMillis > currentEndMillis;
        const shrankStart =
            currentStartMillis !== undefined && startTimeMillis > currentStartMillis + 1;
        const shrankEnd = currentEndMillis !== undefined && endTimeMillis < currentEndMillis - 1;

        // Sticky voiceover handles: a shrunk edge pulls its adjacent overlay in to fill (no exposed
        // waveform); talking-head / AI-avatar don't. Capture neighbours before the resize opens the gap.
        const handlesAreSticky =
            !isTalkingHeadSceneGroup(sceneGroup) && !isAIAvatarSceneGroup(sceneGroup);
        const leftSibling = sceneGroup.scenes[scene.localIndex - 1];
        const rightSibling = sceneGroup.scenes[scene.localIndex + 1];
        const leftNeighbourOverlayId =
            leftSibling && isSceneAnOverlay(state, leftSibling) ? leftSibling.id : null;
        const rightNeighbourOverlayId =
            rightSibling && isSceneAnOverlay(state, rightSibling) ? rightSibling.id : null;

        const moveStart = () =>
            call(
                translateOverlayStartOperation,
                sceneId,
                sceneGroupId,
                baseSceneDesignId,
                startTimeMillis,
                systemTriggered,
                parentActionGroup,
            );
        const moveEnd = () =>
            call(
                translateOverlayEndOperation,
                sceneId,
                sceneGroupId,
                baseSceneDesignId,
                endTimeMillis,
                systemTriggered,
                parentActionGroup,
            );

        call(runVoicedrivenTranscriptBatchOperation, sceneGroupId, () => {
            call(
                moveOverlaysAwayFromTimesOperation,
                sceneGroupId,
                startTimeMillis,
                endTimeMillis,
                [sceneId],
                systemTriggered,
                parentActionGroup,
            );
            if (growingEnd) {
                moveEnd();
                moveStart();
            } else {
                moveStart();
                moveEnd();
            }

            // Sticky fence-post: stretch the adjacent overlay to meet the shrunk edge (voiceover only).
            if (handlesAreSticky && shrankStart && leftNeighbourOverlayId) {
                call(
                    translateOverlayEndOperation,
                    leftNeighbourOverlayId,
                    sceneGroupId,
                    baseSceneDesignId,
                    startTimeMillis,
                    systemTriggered,
                    parentActionGroup,
                );
            }
            if (handlesAreSticky && shrankEnd && rightNeighbourOverlayId) {
                call(
                    translateOverlayStartOperation,
                    rightNeighbourOverlayId,
                    sceneGroupId,
                    baseSceneDesignId,
                    endTimeMillis,
                    systemTriggered,
                    parentActionGroup,
                );
            }
        });

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);
